import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, message, Tag, Input, Select, Card, Row, Col, Statistic } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from '../utils/axios';
import dayjs from 'dayjs';
import { authService } from '../services/authService';
import { getProjects } from '../services/project';


interface StatsData {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  settled: number;
  paid: number;
  todaySubmissions: number;
  pendingRate: string;
}

const QuestionnaireApproval: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>('all');
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [doctorName, setDoctorName] = useState('');
  const [volunteerName, setVolunteerName] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [stats, setStats] = useState<StatsData>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    settled: 0,
    paid: 0,
    todaySubmissions: 0,
    pendingRate: '0%'
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();
  const isProjectAdmin = currentUser?.role === 'project_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // 获取统计数据
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/survey-submissions/stats', { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.data.success) {
        const statsData = res.data.data;
        setStats({
          total: statsData.total || 0,
          pending: statsData.pending || 0,
          approved: statsData.approved || 0,
          rejected: statsData.rejected || 0,
          settled: statsData.settled || 0,
          paid: statsData.paid || 0,
          todaySubmissions: statsData.todaySubmissions || 0,
          pendingRate: statsData.total > 0 ? 
            ((statsData.pending / statsData.total) * 100).toFixed(1) + '%' : '0%'
        });
      }
    } catch (e) {
      console.error('获取统计数据失败:', e);
    } finally {
      setStatsLoading(false);
    }
  };
  //获取项目列表
  const fetchProjects = async () => {

    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.data);
      }
    } catch (error) {
      message.error('获取项目列表失败');
    }
  };

  // 获取问卷提交
  const fetchData = async (statusParam = status, selectedProjectIdParam = selectedProjectId, doctor = doctorName, volunteer = volunteerName, pageParam = currentPage, pageSizeParam = pageSize) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params: any = {
        page: pageParam,
        pageSize: pageSizeParam
      };

      if (statusParam && statusParam !== 'all') params.status = statusParam;
      if (selectedProjectIdParam) params.project_id = selectedProjectIdParam;
      if (doctor) params.doctor_name = doctor;
      if (volunteer) params.volunteer_name = volunteer;
      
      const res = await axios.get('/survey-submissions/page', { 
        params,
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.data.success) {
        setQuestionnaires(res.data.data.list || []);
        setTotal(res.data.data.total || 0);
      } else {
        message.error('获取问卷列表失败');
      }
    } catch (e) {
      console.error('获取问卷列表失败:', e);
      message.error('获取问卷列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(status, selectedProjectId, doctorName, volunteerName, 1, pageSize);
    fetchStats();
    fetchProjects();
  }, [status, selectedProjectId]);

  const handleSearch = () => {
    setCurrentPage(1); // 重置到第一页
    fetchData(status, selectedProjectId, doctorName, volunteerName, 1, pageSize);
  };

  // 分页处理
  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchData(status, selectedProjectId, doctorName, volunteerName, page, size);
  };

  const handleView = async (record: any) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/survey-submissions/${record.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const submission = res.data.data;

      Modal.info({
        title: '问卷详情',
        content: (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>提交时间：{submission.created_at ? dayjs(submission.created_at).format('YYYY-MM-DD HH:mm:ss') : ''}</span>
                <span>状态：<Tag color={submission.status === 'pending' ? 'orange' : submission.status === 'approved' ? 'green' : submission.status === 'rejected' ? 'red' : submission.status === 'settled' ? 'blue' : 'purple'}>
                  {submission.status === 'pending' ? '待审批' : submission.status === 'approved' ? '已通过' : submission.status === 'rejected' ? '已拒绝' : submission.status === 'settled' ? '待支付' : '已支付'}
                </Tag></span>
              </div>
              {submission.approval_remark && (
                <div style={{ marginTop: 8, padding: '8px', background: '#fff', borderRadius: '4px' }}>
                  <div style={{ color: '#666', marginBottom: 4 }}>审批备注：</div>
                  <div>{submission.approval_remark}</div>
                </div>
              )}
            </div>
            {submission.questions && submission.questions.length > 0 ? submission.questions.map((q: any, idx: number) => (
              <div key={q.id} style={{ marginBottom: 16, padding: '12px', background: '#fff', borderRadius: '4px', border: '1px solid #f0f0f0' }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>{idx + 1}. {q.title}</div>
                <div style={{ marginLeft: 16 }}>
                  {(q.type === 'image' || q.type === 'file') ? (
                    <div>
                      {q.answer ? (
                        Array.isArray(q.answer)
                          ? q.answer.map((url: string, i: number) =>
                              url ? (
                                <img 
                                  key={i} 
                                  src={url} 
                                  alt="图片" 
                                  style={{ 
                                    maxWidth: 120, 
                                    maxHeight: 120, 
                                    marginRight: 8, 
                                    borderRadius: 4, 
                                    border: '1px solid #eee',
                                    cursor: 'pointer'
                                  }} 
                                  onClick={() => {
                                    Modal.info({
                                      title: '图片预览',
                                      content: (
                                        <div style={{ textAlign: 'center' }}>
                                          <img 
                                            src={url} 
                                            alt="预览图片" 
                                            style={{ 
                                              maxWidth: '100%', 
                                              maxHeight: '70vh', 
                                              objectFit: 'contain'
                                            }} 
                                          />
                                        </div>
                                      ),
                                      width: 'auto',
                                      centered: true,
                                      maskClosable: true,
                                      okText: '关闭'
                                    });
                                  }}
                                />
                              ) : null
                            )
                          : (
                              <img 
                                src={q.answer} 
                                alt="图片" 
                                style={{ 
                                  maxWidth: 120, 
                                  maxHeight: 120, 
                                  borderRadius: 4, 
                                  border: '1px solid #eee',
                                  cursor: 'pointer'
                                }} 
                                onClick={() => {
                                  Modal.info({
                                    title: '图片预览',
                                    content: (
                                      <div style={{ textAlign: 'center' }}>
                                        <img 
                                          src={q.answer} 
                                          alt="预览图片" 
                                          style={{ 
                                            maxWidth: '100%', 
                                            maxHeight: '70vh', 
                                            objectFit: 'contain'
                                          }} 
                                        />
                                      </div>
                                    ),
                                    width: 'auto',
                                    centered: true,
                                    maskClosable: true,
                                    okText: '关闭'
                                  });
                                }}
                              />
                            )
                      ) : (
                        <span style={{ color: '#aaa' }}>未上传</span>
                      )}
                    </div>
                  ) : q.type === 'text' || q.type === 'textarea' ? (
                    <div style={{ color: '#1677ff' }}>
                      {q.answer ? q.answer : <span style={{ color: '#aaa' }}>未作答</span>}
                    </div>
                  ) : (q.type === 'radio' || q.type === 'checkbox') && q.options ? (
                    <div>
                      {q.options.map((option: any, optIdx: number) => {
                        const isSelected = q.type === 'checkbox' 
                          ? (Array.isArray(q.answer) && q.answer.some((a: any) => 
                              typeof a === 'object' ? a.value === option.value : a === option.value
                            ))
                          : (typeof q.answer === 'object' ? q.answer.value === option.value : q.answer === option.value);
                        
                        return (
                          <div 
                            key={optIdx} 
                            style={{ 
                              marginBottom: 4, 
                              padding: '4px 8px',
                              background: isSelected ? '#e6f7ff' : '#f9f9f9',
                              borderRadius: '4px',
                              border: isSelected ? '1px solid #1677ff' : '1px solid #e8e8e8'
                            }}
                          >
                            <span style={{ 
                              fontWeight: isSelected ? 'bold' : 'normal',
                              color: isSelected ? '#1677ff' : '#666'
                            }}>
                              {option.value || optIdx + 1}. {option.label}
                              {isSelected && <span style={{ marginLeft: 8, color: '#52c41a' }}>✓</span>}
                            </span>
                          </div>
                        );
                      })}
                      {!q.answer && <div style={{ color: '#aaa', marginTop: 8 }}>未作答</div>}
                    </div>
                  ) : q.type === 'sort' && q.options && q.answer ? (
                    <div>
                      <div style={{ marginBottom: 8, fontWeight: 500, color: '#666' }}>用户排序结果：</div>
                      <div style={{ border: '1px solid #e8e8e8', borderRadius: '4px', padding: '12px', background: '#fafafa' }}>
                        {(() => {
                          // 处理排序答案
                          const sortedValues = Array.isArray(q.answer) ? q.answer : [];
                          return sortedValues.map((value: any, index: number) => {
                            // 根据value找到对应的选项
                            const option = q.options.find((opt: any) => opt.value === value);
                            if (!option) return null;
                            
                            return (
                              <div 
                                key={index}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  margin: '4px 0',
                                  backgroundColor: '#fff',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: '4px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                              >
                                <div style={{ 
                                  minWidth: '24px',
                                  height: '24px',
                                  backgroundColor: '#1677ff',
                                  color: 'white',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  marginRight: '12px'
                                }}>
                                  {index + 1}
                                </div>
                                <div style={{ flex: 1 }}>
                                  {option.label}
                                </div>
                              </div>
                            );
                          }).filter(Boolean);
                        })()}
                      </div>
                      {Array.isArray(q.answer) && q.answer.length === 0 && (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>用户未进行排序</div>
                      )}
                    </div>
                  ) : q.type === 'sort' && !q.answer ? (
                    <div style={{ color: '#aaa' }}>未作答</div>
                  ) : (
                    <div style={{ color: '#1677ff' }}>
                      {q.answer ? (
                        typeof q.answer === 'object' 
                          ? JSON.stringify(q.answer) 
                          : q.answer
                      ) : <span style={{ color: '#aaa' }}>未作答</span>}
                    </div>
                  )}
                </div>
              </div>
            )) : <div>无题目</div>}
          </div>
        ),
        width: 800
      });
    } catch (error) {
      message.error('获取问卷详情失败');
    }
  };



  const handleApprove = (id: number) => {
    let remark = '';
    Modal.confirm({
      title: '确认通过',
      content: <Input.TextArea placeholder="审批备注（可选）" onChange={e => remark = e.target.value} />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`/survey-submissions/${id}/approve`, { remark }, { headers: { Authorization: `Bearer ${token}` } });
          message.success('已通过');
          fetchData(status, selectedProjectId, doctorName, volunteerName, currentPage, pageSize);
          fetchStats();
        } catch (error) {
          message.error('审批失败');
        }
      }
    });
  };

  const handleReject = (id: number) => {
    let remark = '';
    Modal.confirm({
      title: '确认拒绝',
      content: <Input.TextArea placeholder="审批备注（可选）" onChange={e => remark = e.target.value} />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`/survey-submissions/${id}/reject`, { remark }, { headers: { Authorization: `Bearer ${token}` } });
          message.success('已拒绝');
          fetchData(status, selectedProjectId, doctorName, volunteerName, currentPage, pageSize);
          fetchStats();
        } catch (error) {
          message.error('审批失败');
        }
      }
    });
  };

  const handleBatchApprove = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要审批的问卷');
      return;
    }

    let remark = '';
    Modal.confirm({
      title: '批量通过',
      content: <Input.TextArea placeholder="审批备注（可选）" onChange={e => remark = e.target.value} />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.post('/survey-submissions/batch/approve', 
            { ids: selectedRowKeys, remark }, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(res.data.data.message);
          setSelectedRowKeys([]);
          fetchData(status, selectedProjectId, doctorName, volunteerName, currentPage, pageSize);
          fetchStats();
        } catch (error) {
          message.error('批量审批失败');
        }
      }
    });
  };

  const handleBatchReject = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要审批的问卷');
      return;
    }

    let remark = '';
    Modal.confirm({
      title: '批量拒绝',
      content: <Input.TextArea placeholder="审批备注（可选）" onChange={e => remark = e.target.value} />,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.post('/survey-submissions/batch/reject', 
            { ids: selectedRowKeys, remark }, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(res.data.data.message);
          setSelectedRowKeys([]);
          fetchData(status, selectedProjectId, doctorName, volunteerName, currentPage, pageSize);
          fetchStats();
        } catch (error) {
          message.error('批量审批失败');
        }
      }
    });
  };

  const columns: ColumnsType<any> = [
    { title: '问卷标题', dataIndex: 'survey_title', key: 'survey_title' },
    { title: '项目名称', dataIndex: 'project_name', key: 'project_name' },
    { title: '志愿者', dataIndex: 'volunteer_name', key: 'volunteer_name' },
    { title: '医生', dataIndex: 'doctor_name', key: 'doctor_name' },
    { title: '提交时间', dataIndex: 'created_at', key: 'created_at', render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (status: string) => {
      const statusMap = { 
        pending: { color: 'orange', text: '待审批' }, 
        approved: { color: 'green', text: '已通过' }, 
        rejected: { color: 'red', text: '已拒绝' },
        settled: { color: 'blue', text: '待支付' },
        paid: { color: 'purple', text: '已支付' } };
      const s = status as keyof typeof statusMap;
      return <Tag color={statusMap[s]?.color}>{statusMap[s]?.text}</Tag>;
    } },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" icon={<EyeOutlined />} onClick={() => handleView(record)}>查看</Button>
          {record.status === 'pending' && <>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(record.id)}>通过</Button>
            <Button type="primary" danger icon={<CloseOutlined />} onClick={() => handleReject(record.id)}>拒绝</Button>
          </>}
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.status !== 'pending',
    }),
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="问卷总数"
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="待审批"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix={
                <span style={{ fontSize: '12px', color: '#999' }}>
                  ({stats.pendingRate})
                </span>
              }
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="已通过"
              value={stats.approved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="已拒绝"
              value={stats.rejected}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="已审核"
              value={stats.settled}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading}>
            <Statistic
              title="已支付"
              value={stats.paid}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 今日统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card 
            title="今日新增" 
            loading={statsLoading}
            style={{ textAlign: 'center' }}
          >
            <Statistic
              title="今日提交问卷数"
              value={stats.todaySubmissions}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: '32px' }}
            />
          </Card>
        </Col>
        <Col span={20}>
          <Card 
            title="审批进度" 
            loading={statsLoading}
            style={{ textAlign: 'center' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {stats.total > 0 ? ((stats.approved / stats.total) * 100).toFixed(1) : 0}%
                </div>
                <div style={{ color: '#999', fontSize: '12px' }}>通过率</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                  {stats.total > 0 ? ((stats.rejected / stats.total) * 100).toFixed(1) : 0}%
                </div>
                <div style={{ color: '#999', fontSize: '12px' }}>拒绝率</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#13c2c2' }}>
                  {stats.total > 0 ? ((stats.settled / stats.total) * 100).toFixed(1) : 0}%
                </div>
                <div style={{ color: '#999', fontSize: '12px' }}>审核率</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                  {stats.total > 0 ? ((stats.paid / stats.total) * 100).toFixed(1) : 0}%
                </div>
                <div style={{ color: '#999', fontSize: '12px' }}>支付率</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

 

      <Space style={{ marginBottom: 16 }}>
        <span>筛选状态：</span>
        <Select
          value={status}
          style={{ width: 120 }}
          onChange={val => {
            setStatus(val);
            setCurrentPage(1); // 状态改变时重置到第一页
          }}
          options={[
            { value: 'all', label: '全部' },
            { value: 'pending', label: '待审批' },
            { value: 'approved', label: '已通过' },
            { value: 'rejected', label: '已拒绝' },
            { value: 'settled', label: '待支付' },
            { value: 'paid', label: '已支付' }
          ]}
        />
        <Select
          style={{ width: 200 }}
          placeholder="选择项目"
          value={selectedProjectId}
          onChange={(value) => {
            setSelectedProjectId(value);
            setCurrentPage(1); // 项目改变时重置到第一页
          }}
          allowClear
        >
          {projects.map((project) => (
            <Select.Option key={project.id} value={project.id}>
              {project.name}
            </Select.Option>
          ))}
        </Select>
        <Input
          placeholder="医生名称"
          value={doctorName}
          onChange={e => setDoctorName(e.target.value)}
          style={{ width: 120 }}
          allowClear
        />
        <Input
          placeholder="志愿者名称"
          value={volunteerName}
          onChange={e => setVolunteerName(e.target.value)}
          style={{ width: 120 }}
          allowClear
        />
        <Button type="primary" onClick={handleSearch}>查询</Button>
      </Space>

      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button type="primary" onClick={handleBatchApprove}>批量通过</Button>
            <Button type="primary" danger onClick={handleBatchReject}>批量拒绝</Button>
            <span style={{ marginLeft: 8 }}>
              已选择 {selectedRowKeys.length} 项
            </span>
          </Space>
        </div>
      )}

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={questionnaires}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange
        }}
      />
    </div>
  );
};

export default QuestionnaireApproval; 